import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import {
  CK, FONT_BODY, FONT_MONO, statusChipSx, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  insetPanelSx, emptyTextSx,
} from '../portalStyles';
import { formatStatusLabel } from '../../../utils/statusLabel';
import { computeLineDiff } from '../../../utils/templateDiff';
import { getInstanceDrift } from '../../../services/stacksetsApi';

// Resource drift status -> chip intent.
const resourceDriftIntent = (s) => ({
  IN_SYNC: 'success',
  MODIFIED: 'warning',
  DELETED: 'error',
}[s] || 'neutral');

const diffRowSx = {
  add: { bgcolor: '#F0F9F4', color: CK.green },
  del: { bgcolor: '#FDF2F2', color: CK.red },
  ctx: { color: CK.muted },
};
const prefix = { add: '+', del: '−', ctx: ' ' };

// Pretty-print a property value: JSON values are re-indented so a policy or
// map diffs line by line; anything else is shown as-is.
const pretty = (v) => {
  if (v === undefined || v === null || v === '') return '';
  try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return String(v); }
};

// The expected vs actual text of one resource, one property per block.
const sideText = (differences, side) => differences
  .map((d) => `${d.propertyPath}:\n${pretty(side === 'expected' ? d.expectedValue : d.actualValue)
    .split('\n').map((l) => `  ${l}`).join('\n')}`)
  .join('\n');

function PropertyDiff({ differences }) {
  const diff = useMemo(
    () => computeLineDiff(sideText(differences, 'expected'), sideText(differences, 'actual')),
    [differences],
  );
  if (diff.tooLarge) {
    return (
      <Typography sx={{ ...emptyTextSx, fontSize: '0.75rem' }}>
        Too large to show ({diff.added} lines added, {diff.removed} removed).
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        fontFamily: FONT_MONO, fontSize: 12, lineHeight: 1.5, border: `1px solid ${CK.border}`,
        borderRadius: '8px', overflow: 'auto', maxHeight: 280, backgroundColor: CK.surface,
      }}
    >
      {diff.rows.map((r, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={idx} sx={{ px: 1, whiteSpace: 'pre', ...diffRowSx[r.type] }}>
          {`${prefix[r.type]} ${r.text}`}
        </Box>
      ))}
    </Box>
  );
}

// ResourceDriftPanel lists one instance's resources as checked by the latest
// drift detection, drifted first, with an expected (−) vs actual (+) line diff
// of the differing properties.
export default function ResourceDriftPanel({ stackSetId, account, region }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInSync, setShowInSync] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getInstanceDrift(stackSetId, account, region);
      setData(res?.data || { resources: [] });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [stackSetId, account, region]);

  useEffect(() => { load(); }, [load]);

  const resources = Array.isArray(data?.resources) ? data.resources : [];
  const drifted = resources.filter((r) => r.driftStatus !== 'IN_SYNC');
  const shown = showInSync ? resources : drifted;

  return (
    <Box sx={{ ...insetPanelSx, p: 2, my: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.875rem', color: CK.navy }}>
          Resource drift
        </Typography>
        {data?.checkedAt && (
          <Typography sx={{ ...emptyTextSx, fontSize: '0.75rem' }}>
            checked {new Date(data.checkedAt).toLocaleString()}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {resources.length > drifted.length && (
          <Button size="small" onClick={() => setShowInSync((v) => !v)}>
            {showInSync ? 'Hide in-sync resources' : `Show in-sync resources (${resources.length - drifted.length})`}
          </Button>
        )}
        <Button size="small" onClick={load} disabled={loading}>Reload</Button>
      </Box>
      {loading && !data && <CircularProgress size={20} />}
      {error && <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>}
      {data && !data.operationId && (
        <Typography sx={emptyTextSx}>Drift has not been checked yet — run Detect drift.</Typography>
      )}
      {data?.operationId && shown.length === 0 && (
        <Typography sx={emptyTextSx}>No drifted resources.</Typography>
      )}
      {shown.length > 0 && (
        <TableContainer sx={{ backgroundColor: CK.surface, borderRadius: '8px', border: `1px solid ${CK.border}` }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={headCellSx}>Logical ID</TableCell>
                <TableCell sx={headCellSx}>Type</TableCell>
                <TableCell sx={headCellSx}>Drift</TableCell>
                <TableCell sx={headCellSx}>Expected (−) vs actual (+)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r, i) => (
                <TableRow key={r.logicalResourceId || i} sx={rowSx(i)}>
                  <TableCell sx={{ ...bodyCellSx, verticalAlign: 'top', wordBreak: 'break-word' }}>
                    {r.logicalResourceId}
                    {r.physicalResourceId && (
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.7rem', color: CK.muted, wordBreak: 'break-all' }}>
                        {r.physicalResourceId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{r.resourceType}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, verticalAlign: 'top' }}>
                    <Chip size="small" label={formatStatusLabel(r.driftStatus)} sx={statusChipSx(resourceDriftIntent(r.driftStatus))} />
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, minWidth: 280, maxWidth: 560 }}>
                    {(r.differences || []).length > 0
                      ? <PropertyDiff differences={r.differences} />
                      : <Typography sx={{ ...emptyTextSx, fontSize: '0.8rem' }}>{r.driftStatus === 'DELETED' ? 'Resource deleted' : '—'}</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
